import { Table } from 'antd'
import { useMemo, useState, type Key } from 'react'
import { MdOutlineSpeed } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'
import { useLayoutHeaderHeight } from '@/Ship/Hooks/useLayoutHeaderHeight'
import {
    formatKm,
    formatKmDelta,
    formatPercentDelta,
} from '@/Containers/ShiftSection/ShiftContainer/Helpers/shiftFormat'

import { GroupName } from '../Components/GroupName'
import { MileageFilters } from '../Components/MileageFilters'
import { MileageShifts } from '../Components/MileageShifts'
import { ReportExportButton } from '../Components/ReportExportButton'
import { ReportShell } from '../Components/ReportShell'
import { ReportSummary, type ReportKpi } from '../Components/ReportSummary'
import { ReportError } from '../Components/ReportTableState'
import type { MileageFilterState, MileageReport, MileageRow } from '../Contracts/Report'
import { buildMileageParams, initialMileageFilters, mileageShellChips } from '../Helpers/mileageQuery'
import { groupKeyParam, groupLabel } from '../Helpers/reportGroups'
import { formatPeriodLabel } from '../Helpers/reportPeriod'
import { useReportFetch } from '../Hooks/useReportFetch'
import { useTableSort } from '../Hooks/useTableSort'

import styles from '../Components/Report.module.css'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

import '@/Ship/Templates/CrudResource/Table.css'

/** Колонки з сортуванням (ключі — як `sort` в експорті, MileageReportExportAction::SORT_FIELDS) */
type MileageSortKey =
    | 'group'
    | 'shifts'
    | 'mileage'
    | 'wialonMileage'
    | 'deviation'
    | 'deviationPercent'
    | 'awaitingReview'

const byNumber =
    (field: 'shifts' | 'mileage' | 'wialonMileage' | 'deviation' | 'awaitingReview') =>
    (a: MileageRow, b: MileageRow) =>
        a[field] - b[field]

// Без відсотка (пробіг Wialon 0) — найменшим, як в експорті; без віднімання, бо −∞ − −∞ = NaN
const byPercent = (a: MileageRow, b: MileageRow): number => {
    const x = a.deviationPercent ?? -Infinity
    const y = b.deviationPercent ?? -Infinity
    return x === y ? 0 : x < y ? -1 : 1
}

const num = (text: string) => <span className={cx(styles, 'num')}>{text}</span>

const shiftsWord = (count: number): string => pluralizeUk(count, ['зміна', 'зміни', 'змін'])

/**
 * Звіт «Контроль пробігу»: завершені зміни за період (дата зміни) у розрізі водія — пробіг за
 * одометром, за Wialon і відхилення (одометр − Wialon). Рядок розгортається в зміни водія.
 */
const MileageControlPage = () => {
    const [filters, setFilters] = useState<MileageFilterState>(initialMileageFilters)
    const [expandedKeys, setExpandedKeys] = useState<Key[]>([])
    // Сортування кероване: експорт віддає рядки в тому ж порядку, що й таблиця
    const { sortOrder, onChange: onSortChange, sortParams } = useTableSort<MileageSortKey, MileageRow>()
    const headerHeight = useLayoutHeaderHeight()

    const params = useMemo(() => buildMileageParams(filters), [filters])
    const { data, loading, error, reload } = useReportFetch<MileageReport>('/services/reports/mileage-control', params)

    // Інші фільтри — інші зміни в деталізації: розгорнуті рядки згортаємо
    const changeFilters = (next: MileageFilterState) => {
        setExpandedKeys([])
        setFilters(next)
    }

    const rows = data?.items ?? []
    const totals = data?.totals
    const kpis: ReportKpi[] = [
        {
            key: 'shifts',
            label: 'Змін',
            value: (totals?.shifts ?? 0).toLocaleString('uk-UA'),
            hint: totals?.shiftsWithoutData ? `без даних Wialon чи одометра: ${totals.shiftsWithoutData}` : undefined,
        },
        { key: 'mileage', label: 'Пробіг за зміною', value: formatKm(totals?.mileage ?? 0) },
        { key: 'wialon', label: 'Пробіг за Wialon', value: formatKm(totals?.wialonMileage ?? 0) },
        {
            key: 'deviation',
            label: 'Відхилення',
            value: formatKmDelta(totals?.deviation ?? 0),
            hint:
                totals?.deviationPercent !== null && totals?.deviationPercent !== undefined
                    ? formatPercentDelta(totals.deviationPercent)
                    : undefined,
        },
        { key: 'review', label: 'Потребує перевірки', value: (totals?.awaitingReview ?? 0).toLocaleString('uk-UA') },
    ]

    const columns = [
        {
            title: 'Водій',
            key: 'group',
            sortOrder: sortOrder('group'),
            sorter: (a: MileageRow, b: MileageRow) =>
                (groupLabel('driver', a) ?? '').localeCompare(groupLabel('driver', b) ?? '', 'uk'),
            render: (_: unknown, row: MileageRow) => <GroupName groupBy="driver" row={row} />,
        },
        {
            title: 'Змін',
            dataIndex: 'shifts',
            key: 'shifts',
            width: 110,
            align: 'right' as const,
            sortOrder: sortOrder('shifts'),
            sorter: byNumber('shifts'),
            render: (value: number, row: MileageRow) => (
                <span
                    className={cx(styles, 'num')}
                    title={row.shiftsWithoutData ? 'Без пробігу Wialon чи одометра — не в сумах' : undefined}
                >
                    {value}
                    {row.shiftsWithoutData > 0 && (
                        <span className={cx(styles, 'faint')}> · без даних {row.shiftsWithoutData}</span>
                    )}
                </span>
            ),
        },
        {
            title: 'Пробіг за зміною',
            dataIndex: 'mileage',
            key: 'mileage',
            width: 150,
            align: 'right' as const,
            sortOrder: sortOrder('mileage'),
            sorter: byNumber('mileage'),
            render: (value: number) => num(formatKm(value)),
        },
        {
            title: 'Пробіг за Wialon',
            dataIndex: 'wialonMileage',
            key: 'wialonMileage',
            width: 150,
            align: 'right' as const,
            sortOrder: sortOrder('wialonMileage'),
            sorter: byNumber('wialonMileage'),
            render: (value: number) => num(formatKm(value)),
        },
        {
            title: 'Відхилення',
            dataIndex: 'deviation',
            key: 'deviation',
            width: 130,
            align: 'right' as const,
            sortOrder: sortOrder('deviation'),
            sorter: byNumber('deviation'),
            render: (value: number) => num(formatKmDelta(value)),
        },
        {
            title: 'Відхилення, %',
            dataIndex: 'deviationPercent',
            key: 'deviationPercent',
            width: 130,
            align: 'right' as const,
            sortOrder: sortOrder('deviationPercent'),
            sorter: byPercent,
            render: (value: number | null, row: MileageRow) =>
                value === null ? (
                    <span
                        className={cx(styles, 'faint')}
                        title={
                            row.shiftsWithoutData === row.shifts
                                ? 'Немає змін з обома пробігами'
                                : 'Пробіг Wialon 0 — відсоток не рахується'
                        }
                    >
                        —
                    </span>
                ) : (
                    num(formatPercentDelta(value))
                ),
        },
        {
            title: 'Потребує перевірки',
            dataIndex: 'awaitingReview',
            key: 'awaitingReview',
            width: 150,
            align: 'right' as const,
            sortOrder: sortOrder('awaitingReview'),
            sorter: byNumber('awaitingReview'),
            render: (value: number) =>
                value > 0 ? (
                    <span className={cx(styles, 'num')}>
                        <span className={cx(styles, 'delayed')}>{value}</span>
                    </span>
                ) : (
                    <span className={cx(styles, 'faint')}>0</span>
                ),
        },
    ]

    const subtitle = totals
        ? `${formatPeriodLabel(filters.period)} · ${totals.shifts} ${shiftsWord(totals.shifts)}`
        : formatPeriodLabel(filters.period)

    return (
        <ReportShell
            title="Контроль пробігу"
            subtitle={subtitle}
            filterPanel={<MileageFilters value={filters} onChange={changeFilters} />}
            chips={mileageShellChips(filters, changeFilters)}
            onClearFilters={() => changeFilters(initialMileageFilters())}
            onRefresh={reload}
            refreshing={loading}
            storageKey="autostrada.report.mileage.filterOpen"
            actions={
                <ReportExportButton
                    url="/services/reports/mileage-control/export"
                    params={{ ...params, ...sortParams }}
                    fallbackFileName="Контроль пробігу.xlsx"
                />
            }
        >
            <ReportSummary items={kpis} loading={loading && !data} />

            <div className={cx(styles, 'toolbar')}>
                <span className={cx(styles, 'toolbarHint')}>
                    Відхилення = пробіг за одометром − пробіг за Wialon. Клік по рядку розгортає зміни водія
                </span>
            </div>

            {error ? (
                <ReportError message={error} onRetry={reload} />
            ) : (
                <Table<MileageRow>
                    className={`wl-table ${cx(styles, 'table')}`}
                    size="middle"
                    rowKey={groupKeyParam}
                    loading={loading}
                    columns={columns}
                    dataSource={rows}
                    onChange={onSortChange}
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: false,
                        showTotal: (total, range) => (total > 0 ? `${range[0]}–${range[1]} з ${total}` : ''),
                    }}
                    scroll={{ x: 'max-content' }}
                    sticky={{ offsetHeader: headerHeight }}
                    locale={{
                        emptyText: loading ? (
                            ' '
                        ) : (
                            <div className={cx(styles, 'empty')}>
                                <p className={cx(styles, 'emptyTitle')}>Немає завершених змін за цими умовами</p>
                                <p className={cx(styles, 'emptyHint')}>
                                    Спробуйте розширити період, змінити межі відхилення або зняти частину фільтрів.
                                </p>
                            </div>
                        ),
                    }}
                    rowClassName={() => 'wl-table-row-clickable'}
                    expandable={{
                        expandedRowKeys: expandedKeys,
                        onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                        expandRowByClick: true,
                        expandedRowRender: (row) => <MileageShifts groupKey={groupKeyParam(row)} params={params} />,
                    }}
                />
            )}
        </ReportShell>
    )
}

export default MileageControlPage

export const pageConfig: IPageConfig = {
    route: '/reports/mileage-control',
    key: 'Report.MileageControl',
    title: 'Контроль пробігу',
    icon: <MdOutlineSpeed />,
}
